/**
 * Coachmark - Non-blocking guided tutorial with arrows
 * Highlights sections with animated arrows and minimal UI
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useTutorial } from '@/contexts/TutorialContext';
import { Button } from '@/components/ui/button';
import { ArrowDown, ArrowRight, ArrowUp, ArrowLeft, X, ChevronRight, ChevronLeft, MoreVertical, List, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
// CORE-BL: the badge denominator comes from the sequence actually being
// walked, not from the raw track.
import { ChecklistDialog } from './ChecklistDialog';
import { useFormFactor } from '@/hooks/use-mobile';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface CoachmarkProps {
  stepId: string;
  targetSelector?: string; // data-testid selector
  arrowPosition?: 'top' | 'bottom' | 'left' | 'right';
  arrowOffset?: number; // Pixels from edge
}

export const Coachmark = ({ 
  stepId, 
  targetSelector, 
  arrowPosition = 'top',
  arrowOffset = 50,
}: CoachmarkProps) => {
  const {
    isActive,
    flow,
    currentStep,
    currentStepData,
    nextStep,
    previousStep,
    dismissTutorial,
    replayTutorial,
    checkCurrentGate,
    completeTutorial,
    experienceLabel,
    isMinimized,
    toggleMinimize,
    activeSteps,
} = useTutorial();

  const navigate = useNavigate();
  const formFactor = useFormFactor();
  const isCompactMode = formFactor === 'phone';

  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [secondaryTargetRect, setSecondaryTargetRect] = useState<DOMRect | null>(null);
  const [isGateSatisfied, setIsGateSatisfied] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);

  // Check if this coachmark should be shown
  const isCurrentStep = isActive && currentStepData?.id === stepId;

  // Update target element position (supports multiple targets with union + dynamic popup detection)
  const updateTargetPosition = useCallback(() => {
    // Explicitly disable spotlight for the manager step "birdseye-tour"
    if (stepId === 'birdseye-tour') {
      setTargetRect(null);
      setSecondaryTargetRect(null);
      return;
    }

    // Special handling for step 14 - check if customer popup is visible
    if (stepId === 'ticket-information') {
      const popup = document.querySelector(`[data-testid="customer-info-popup"]`);
      if (popup) {
        // Popup is open - spotlight it instead of the ticket sections
        setTargetRect(popup.getBoundingClientRect());
        setSecondaryTargetRect(null);
        return;
      }
    }

    // Build a list of possible selectors with sensible fallbacks
    const candidates = [
      targetSelector,
      ...(currentStepData?.coachmarks?.filter((s) => s && s !== targetSelector) || []),
    ].filter(Boolean) as string[];

    if (candidates.length === 0) {
      setTargetRect(null);
      setSecondaryTargetRect(null);
      return;
    }

    // Find ALL matching elements
    const elements: Element[] = [];
    for (const sel of candidates) {
      const el = document.querySelector(`[data-testid="${sel}"]`);
      if (el) elements.push(el);
    }

    if (elements.length === 0) {
      setTargetRect(null);
      setSecondaryTargetRect(null);
      return;
    }

    // If multiple elements, compute the bounding box that encompasses all of them
    if (elements.length > 1) {
      const rects = elements.map(el => el.getBoundingClientRect());
      const minX = Math.min(...rects.map(r => r.left));
      const minY = Math.min(...rects.map(r => r.top));
      const maxX = Math.max(...rects.map(r => r.right));
      const maxY = Math.max(...rects.map(r => r.bottom));

      // Create a unified DOMRect
      const unifiedRect = new DOMRect(minX, minY, maxX - minX, maxY - minY);
      setTargetRect(unifiedRect);
      setSecondaryTargetRect(null);
    } else {
      // Single element - use its rect directly
      setTargetRect(elements[0].getBoundingClientRect());
      setSecondaryTargetRect(null);
    }
  }, [targetSelector, currentStepData, stepId]);

  // Check gate status WITHOUT auto-progression
  // ONB-R69-FIX (HIGH-2): previously errored gate checks auto-flipped the
  // button to "satisfied", letting a user click Next past hard gates (e.g.
  // general-info, vehicle-packages, shop-hours, tech-proficiencies,
  // invite-member) on a single transient network blip. A user could land on
  // the main app with `shop_profile.name` empty or no bays created, and the
  // tutorial itself would record `steps_completed` for steps that weren't
  // actually done — making the completion flag lie. Keep the previous
  // isGateSatisfied value on error (don't punish a user who already met the
  // gate for a transient glitch), and rely on the existing 2-minute stuck
  // timeout below to recover anyone who truly gets stuck.
  const checkGate = useCallback(async () => {
    try {
      const satisfied = await checkCurrentGate();
      setIsGateSatisfied(satisfied);
    } catch (error) {
      console.error('Gate check error:', error);
      // Intentionally do NOT setIsGateSatisfied here — preserve prior state.
    }
  }, [checkCurrentGate]);

  // Update position on mount and when target changes + monitor for popup
  useEffect(() => {
    if (!isCurrentStep) return;

    updateTargetPosition();
    checkGate();

    // Re-check position on resize/scroll
    const handleUpdate = () => {
      updateTargetPosition();
    };

    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate, true);

    // Check gate status periodically to update button state
    const gateInterval = setInterval(checkGate, 1000);
    
    // For step 14, use faster polling to catch popup show/hide
    const pollInterval = stepId === 'ticket-information' ? 100 : 500;
    const positionInterval = setInterval(updateTargetPosition, pollInterval);

    // Safety timeout - if stuck on same step for 2 minutes, enable Next button
    const stuckTimeout = setTimeout(() => {
      console.warn(`Tutorial step ${stepId} timeout - enabling Next button`);
      setIsGateSatisfied(true);
    }, 120000); // 2 minutes

    return () => {
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate, true);
      clearInterval(gateInterval);
      clearInterval(positionInterval);
      clearTimeout(stuckTimeout);
    };
}, [isCurrentStep, updateTargetPosition, checkGate, stepId]);

// Watch for dialog open state to avoid double overlays
useEffect(() => {
  if (typeof document === 'undefined') return;

  const check = () => {
    const openDialog = document.querySelector('[role="dialog"][data-state="open"]');
    setIsDialogOpen(!!openDialog);
  };

  // Initial check
  check();

  const observer = new MutationObserver(check);
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-state'] });

  return () => observer.disconnect();
}, []);

// Auto-minimize on compact mode on first visit
useEffect(() => {
  if (isCompactMode && isActive && !localStorage.getItem('tutorial-mobile-seen')) {
    if (!isMinimized) {
      toggleMinimize();
    }
    localStorage.setItem('tutorial-mobile-seen', 'true');
  }
}, [isCompactMode, isActive, isMinimized, toggleMinimize]);

  // Track if we've already scrolled for the current step
  const hasScrolledForStepRef = useRef<number | null>(null);

  // Phase 2: Snap to spotlight - smooth scroll when step changes (only once per step)
  useEffect(() => {
    if (!isCurrentStep || !targetRect || isMinimized) return;
    
    // Only scroll once per step - if we've already scrolled for this step, skip
    if (hasScrolledForStepRef.current === currentStep) return;

    // Small delay to let DOM settle after navigation
    const scrollTimeout = setTimeout(() => {
      // Mark that we've scrolled for this step
      hasScrolledForStepRef.current = currentStep;
      
      // Calculate scroll position
      const headerHeight = 64; // TopBar height
      const extraPadding = isCompactMode ? 100 : 60; // More room on compact mode for fingers
      const targetTop = targetRect.top + window.scrollY;
      const scrollTarget = Math.max(0, targetTop - headerHeight - extraPadding);

      // Only scroll if target is not already visible
      const viewportTop = window.scrollY + headerHeight;
      const viewportBottom = window.scrollY + window.innerHeight - 150; // Account for tutorial bar

      if (targetTop < viewportTop || targetTop > viewportBottom) {
        window.scrollTo({ top: scrollTarget, behavior: 'smooth' });
      }
    }, 150); // 150ms delay for DOM settle

    return () => clearTimeout(scrollTimeout);
  }, [currentStep, isMinimized, targetRect, isCompactMode, isCurrentStep]);






  // Handle navigation to correct page (no auto-advance - user must click target element)
  useEffect(() => {
    if (!isCurrentStep || !currentStepData) return;

    // Exceptions: do not force-stick to a page for navigation/cross-page actions
    // so user clicks can navigate away without being pulled back.
    if (
      currentStepData.id === 'click-profile' || 
      currentStepData.id === 'save-profile' || 
      currentStepData.id === 'click-birdseye' || 
      currentStepData.id === 'birdseye-navigation' || 
      currentStepData.id === 'click-configure-layout' || 
      currentStepData.id === 'click-team' ||
      currentStepData.id === 'navigate-to-profile' ||
      currentStepData.id === 'click-technicians-tab' ||
      currentStepData.id === 'click-edit-tech' ||
      currentStepData.id === 'create-ticket-button' ||
      currentStepData.id === 'other-pages' ||
      currentStepData.id === 'technician-passport-intro' ||
      currentStepData.id === 'technician-passport-details' ||
      currentStepData.id === 'optiboard-intro' ||
      currentStepData.id === 'account-settings-intro' ||
      currentStepData.id === 'account-settings-review' ||
      currentStepData.id === 'user-guide-intro-user' ||
      currentStepData.id === 'user-guide-review' ||
      currentStepData.id === 'user-tutorial-complete'
    ) return;
    
    const targetPage = currentStepData.page;
    if (!targetPage) return;
    
    const [basePath, queryString] = targetPage.split('?');
    const currentPath = location.pathname;

    // Consider query params: only "on correct page" if required params match too
    let isOnCorrectPage = false;
    if (currentPath === basePath) {
      if (!queryString) {
        isOnCorrectPage = true;
      } else {
        const targetParams = new URLSearchParams(queryString);
        const currentParams = new URLSearchParams(location.search);
        isOnCorrectPage = Array.from(targetParams.entries()).every(
          ([k, v]) => currentParams.get(k) === v
        );
      }
    }
    
    if (!isOnCorrectPage) {
      navigate(targetPage);
    }
  }, [isCurrentStep, currentStepData, location.pathname, location.search, navigate]);

  // The completion notice is NOT owned here any more. Finishing sets
  // `completed`, which makes `isActive` false, and `GlobalTutorialBar`
  // renders coachmarks under `isActive && steps.map(...)` — so the parent
  // unmounted this component at the exact moment a walkthrough completed,
  // taking the dialog with it. A child cannot outlive its parent unmounting
  // it, so the notice moved to `TutorialCompletionNotice`, mounted above
  // that condition. See `completionNotice` in TutorialContext.
  if (!isCurrentStep || !currentStepData) return null;

  const steps = activeSteps;
  const totalSteps = steps.length;
  const progressPercent = totalSteps > 0 ? ((currentStep + 1) / totalSteps) * 100 : 0;

  // Calculate arrow position and rotation
  const getArrowStyle = (): React.CSSProperties | null => {
    if (!targetRect) return null;

    const style: React.CSSProperties = {
      position: 'fixed',
      zIndex: 60,
      pointerEvents: 'none',
    };

    switch (arrowPosition) {
      case 'top':
        style.left = `${targetRect.left + arrowOffset}px`;
        style.bottom = `${window.innerHeight - targetRect.top + 20}px`;
        break;
      case 'bottom':
        style.left = `${targetRect.left + arrowOffset}px`;
        style.top = `${targetRect.bottom + 20}px`;
        break;
      case 'left':
        style.right = `${window.innerWidth - targetRect.left + 20}px`;
        style.top = `${targetRect.top + arrowOffset}px`;
        break;
      case 'right':
        style.left = `${targetRect.right + 20}px`;
        style.top = `${targetRect.top + arrowOffset}px`;
        break;
    }

    return style;
  };

  const getArrowIcon = () => {
    switch (arrowPosition) {
      case 'top':
        return <ChevronDown className="h-8 w-8 text-primary animate-pulse drop-shadow-lg" />;
      case 'bottom':
        return <ChevronUp className="h-8 w-8 text-primary animate-pulse drop-shadow-lg" />;
      case 'left':
        return <ChevronRight className="h-8 w-8 text-primary animate-pulse drop-shadow-lg" />;
      case 'right':
        return <ChevronLeft className="h-8 w-8 text-primary animate-pulse drop-shadow-lg" />;
      default:
        return <ChevronDown className="h-8 w-8 text-primary animate-pulse drop-shadow-lg" />;
    }
  };

  const arrowStyle = getArrowStyle();

  // Steps that should show arrow but no dimming overlay
  const noOverlaySteps = ['admin-settings-tab', 'account-settings-review', 'user-guide-review'];
  const showOverlay = !noOverlaySteps.includes(stepId);

  // Use lighter opacity for edit-bay step to match other steps
  const overlayOpacity = stepId === 'edit-bay' ? 0.15 : 0.4;

  return (
<>
      {/* Spotlight effect - dims everything except target(s) */}
      {!isDialogOpen && targetRect && !isMinimized && showOverlay && (
        <>
          {/* Overlay with cutout using box-shadow (click-through) */}
          <div
            className="fixed pointer-events-none z-[10000]"
            style={{
              left: `${targetRect.left - 8}px`,
              top: `${targetRect.top - 8}px`,
              width: `${targetRect.width + 16}px`,
              height: `${targetRect.height + 16}px`,
              borderRadius: '8px',
              boxShadow: `
                inset 0 0 0 2px hsl(var(--primary) / 0.5),
                0 0 0 9999px rgba(0, 0, 0, ${overlayOpacity})
              `,
              background: 'transparent',
            }}
            data-testid={`spotlight-${stepId}`}
          />
          {/* Pulsing ring effect around spotlight */}
          <div
            className="fixed pointer-events-none z-[9999] animate-tutorial-pulse-ring"
            style={{
              left: `${targetRect.left - 12}px`,
              top: `${targetRect.top - 12}px`,
              width: `${targetRect.width + 24}px`,
              height: `${targetRect.height + 24}px`,
              borderRadius: '12px',
              border: '2px solid hsl(var(--primary) / 0.6)',
            }}
          />
          {/* Secondary target highlight if exists */}
          {secondaryTargetRect && (
            <div
              className="fixed pointer-events-none z-[10001]"
              style={{
                left: `${secondaryTargetRect.left - 8}px`,
                top: `${secondaryTargetRect.top - 8}px`,
                width: `${secondaryTargetRect.width + 16}px`,
                height: `${secondaryTargetRect.height + 16}px`,
                borderRadius: '8px',
                boxShadow: `inset 0 0 0 2px hsl(var(--primary) / 0.5)`
              }}
              data-testid={`spotlight-secondary-${stepId}`}
            />
          )}
        </>
      )}

      {/* Animated arrow pointing to target */}
      {!isDialogOpen && arrowStyle && !isMinimized && (
        <div style={arrowStyle} data-testid={`arrow-${stepId}`}>
          {getArrowIcon()}
        </div>
      )}

      {/* Minimized floating badge */}
      {isMinimized && (
        <button
          onClick={toggleMinimize}
          className="fixed bottom-4 right-4 z-[10000] mb-safe-bottom flex items-center justify-center w-14 h-14 sm:w-12 sm:h-12 bg-primary text-primary-foreground rounded-full shadow-lg hover:scale-110 transition-transform"
          data-testid="tutorial-expand-button"
          data-floating-control="tutorial-expand"
          aria-label="Expand tutorial"
        >
          <span className="text-sm font-bold">{currentStep + 1}/{totalSteps}</span>
        </button>
      )}

      {/* Enhanced control bar with all tutorial controls */}
      {!isMinimized && (
        <div 
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[10000] pointer-events-auto flex items-center bg-card border border-border rounded-2xl shadow-2xl ${
            isCompactMode 
              ? 'px-3 py-2 gap-2 max-w-[calc(100%-1rem)] text-sm' 
              : 'px-5 py-4 gap-4 max-w-3xl w-[calc(100%-2rem)] sm:w-auto'
          }`}
          data-testid={`coachmark-${stepId}`}
        >
          {/* Progress indicator */}
          <div className={`relative flex-shrink-0 ${isCompactMode ? 'w-10 h-10' : 'w-12 h-12'}`}>
            <svg className={isCompactMode ? 'w-10 h-10 transform -rotate-90' : 'w-12 h-12 transform -rotate-90'}>
              <circle
                cx={isCompactMode ? "20" : "24"}
                cy={isCompactMode ? "20" : "24"}
                r={isCompactMode ? "16" : "20"}
                stroke="currentColor"
                strokeWidth="3"
                fill="none"
                className="text-muted"
              />
              <circle
                cx={isCompactMode ? "20" : "24"}
                cy={isCompactMode ? "20" : "24"}
                r={isCompactMode ? "16" : "20"}
                stroke="currentColor"
                strokeWidth="3"
                fill="none"
                strokeDasharray={`${2 * Math.PI * (isCompactMode ? 16 : 20)}`}
                strokeDashoffset={`${2 * Math.PI * (isCompactMode ? 16 : 20) * (1 - progressPercent / 100)}`}
                className="text-primary transition-all duration-300"
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={`font-bold text-primary ${isCompactMode ? 'text-xs' : 'text-sm'}`}>
                {currentStep + 1}/{totalSteps}
              </span>
            </div>
          </div>

          {/* Step info */}
          <div className="flex flex-col min-w-0 flex-1">
            <div className={`font-semibold text-foreground truncate ${isCompactMode ? 'text-xs' : 'text-sm'}`}>
              {currentStepData.title}
            </div>
            {!isCompactMode && (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div 
                      className="text-xs text-muted-foreground line-clamp-2 cursor-help pointer-events-auto"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onPointerDown={(e) => e.stopPropagation()}
                    >
                      {currentStepData.description}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-sm z-[10001]">
                    <p className="text-sm">{currentStepData.description}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            {/* Checklist button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowChecklist(true)}
              onPointerDown={(e) => e.stopPropagation()}
              className="gap-1 hidden sm:flex pointer-events-auto"
              title="View Checklist"
            >
              <List className="w-4 h-4" />
              Checklist
            </Button>

            {/* Minimize button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMinimize}
              onPointerDown={(e) => e.stopPropagation()}
              className="h-9 w-9 pointer-events-auto"
              title="Minimize tutorial"
            >
              <ChevronDown className="w-4 h-4" />
            </Button>

            {/* Navigation */}
            <Button
              onClick={previousStep}
              onPointerDown={(e) => e.stopPropagation()}
              variant="outline"
              size="sm"
              disabled={currentStep === 0}
              className="gap-1 pointer-events-auto"
              data-testid={`coachmark-${stepId}-previous`}
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Previous</span>
            </Button>
            
            {currentStep >= totalSteps - 1 ? (
              <Button
                onClick={() => {
                  // The one action. It records the completion; the notice it
                  // raises is rendered by `TutorialCompletionNotice`, which is
                  // mounted above the `isActive` condition that unmounts this
                  // component the instant completion lands.
                  void completeTutorial();
                }}
                size="sm"
                className="gap-1"
                data-testid={`coachmark-${stepId}-complete`}
              >
                Complete
                <CheckCircle2 className="w-4 h-4" />
              </Button>
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Button
                        onClick={nextStep}
                        size="sm"
                        disabled={currentStep >= totalSteps - 1}
                        className="gap-1"
                        data-testid={`coachmark-${stepId}-next`}
                      >
                        <span className="hidden sm:inline">Next</span>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </TooltipTrigger>
                  {!isGateSatisfied && (
                    <TooltipContent side="top">
                      <p className="text-xs">Click anywhere outside to continue</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            )}

            {/* More menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onPointerDown={(e) => e.stopPropagation()}
                  className="h-9 w-9 pointer-events-auto"
                  title="More options"
                >
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-popover z-[80] mb-2">
                <DropdownMenuItem onClick={() => setShowChecklist(true)}>
                  View Checklist
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="coachmark-replay"
                  onClick={async () => {
                    // `navigate('/')` was route-blind. An org admin's
                    // walkthrough begins on `/user-guide`, and `/` answers
                    // them with a 403 — so replaying it sent them to a wall.
                    // And it navigated whether or not the replay succeeded.
                    const result = await replayTutorial();
                    if (result.status !== 'started') return;
                    // The active-step effect above already navigates to the
                    // current step's own page, so there is nothing to guess.
                  }}
                >
                  Replay from Start
                </DropdownMenuItem>
                <DropdownMenuItem onClick={dismissTutorial}>
                  Dismiss Tutorial
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}

      <ChecklistDialog 
        open={showChecklist}
        onOpenChange={setShowChecklist}
      />

    </>
  );
};
